"use client";

import { FadeIn, FadeInStaggerItem, Stagger } from "@/components/ui/motion";
import TiltCard from "@/components/ui/TiltCard";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { CaseStudy } from "@/app/actions/case-studies";

interface SelectedWorksProps {
    projects: CaseStudy[];
}

export default function SelectedWorks({ projects }: SelectedWorksProps) {
    // Logic to determine if a project is a mobile app (3 cards per row on large screens) 
    // vs a website/desktop software (2 cards per row on large screens)
    const isMobileApp = (project: CaseStudy) => {
        return (
            project.playStoreLink ||
            project.appStoreLink ||
            (project.category && project.category.toLowerCase().includes("mobile"))
        );
    };

    // Sort projects to group mobile apps together
    const sortedProjects = [...projects].sort((a, b) => {
        const aMobile = isMobileApp(a);
        const bMobile = isMobileApp(b);
        if (aMobile && !bMobile) return -1;
        if (!aMobile && bMobile) return 1;
        return 0;
    });

    return (
        <section className="py-24 bg-transparent border-t border-gray-100 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <FadeIn>
                    <div className="flex flex-col md:flex-row justify-between items-end gap-6 mb-16">
                        <div className="max-w-xl">
                            <h2 className="text-4xl md:text-5xl font-black mb-6 uppercase tracking-tighter">
                                Selected Works<span className="text-primary">.</span>
                            </h2>
                            <p className="text-gray-500 text-lg md:text-xl leading-relaxed font-medium">
                                A curated showcase of digital excellence, spanning AI-driven platforms,
                                high-performance mobile apps, and enterprise ecosystems.
                            </p>
                        </div>
                        <Link
                            href="/case-studies"
                            className="hidden md:flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:text-primary transition-colors group"
                        >
                            Explore all works
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </FadeIn>

                {/* Bento Grid */}
                <Stagger className="grid grid-cols-12 gap-6 md:gap-8">
                    {sortedProjects.map((project, index) => {
                        const isMobile = isMobileApp(project);
                        // Only treat first 3 mobile projects as mobile layout (4 columns)
                        // All others (including extra mobile projects) use web layout (6 columns)
                        const useMobileLayout = isMobile && index < 3;

                        return (
                            <FadeInStaggerItem
                                key={project.id}
                                className={`${useMobileLayout
                                    ? "col-span-12 md:col-span-6 lg:col-span-4"
                                    : "col-span-12 lg:col-span-6"
                                    }`}
                            >
                                <Link href={`/case-studies/${project.id}`} className="block h-full">
                                    <TiltCard
                                        className="h-full bg-white border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-500 group relative overflow-hidden rounded-2xl"
                                        innerClassName="h-full flex flex-col"
                                    >

                                        {/* Content Area - Moved to top for alignment */}
                                        <div className="flex flex-col mb-8 relative z-10">
                                            <div className="flex items-center gap-3 mb-4">
                                                <span className="h-[2px] w-8 bg-black transition-all group-hover:w-12" />
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                                                    {project.category}
                                                </span>
                                            </div>
                                            <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tighter leading-tight">
                                                {project.title}
                                            </h3>
                                        </div>

                                        {/* Background Decor */}
                                        <div className="absolute top-0 right-0 p-4 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity">
                                            <span className="text-6xl font-black uppercase tracking-tighter pointer-events-none select-none">{project.category}</span>
                                        </div>

                                         {/* Image Area - Fixed height on desktop makes mobile (col-span-4) look portrait and web (col-span-6) look landscape */}
                                        <div className="w-full h-[300px] lg:h-[500px] mb-8 relative">
                                            {/* Blurred shadow image layer */}
                                            {project.coverImage && (
                                                <div className="absolute inset-0 transform scale-95 translate-y-4 blur-2xl opacity-60 z-0 pointer-events-none transition-transform duration-300 group-hover:translate-y-6">
                                                    <Image src={project.coverImage} alt="" fill className="object-cover rounded-2xl" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" unoptimized />
                                                </div>
                                            )}
                                            {/* Actual image layer */}
                                            <div className="w-full h-full bg-gray-50 overflow-hidden relative transition-all duration-700 rounded-2xl ring-1 ring-black/5 z-10">
                                                {project.coverImage ? (
                                                    <Image
                                                        src={project.coverImage}
                                                        alt={project.title}
                                                        fill
                                                        className="object-cover transition-transform duration-700"
                                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                        unoptimized
                                                    />
                                                ) : (
                                                    <div className="absolute inset-0 flex items-center justify-center text-gray-300 font-mono text-xs uppercase tracking-widest bg-gray-50">
                                                        Project Preview
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        </div>

                                        {/* CTA at Bottom */}
                                        <div className="mt-auto flex items-center text-[11px] font-black uppercase tracking-[0.25em] group-hover:gap-4 gap-3 transition-all text-black">
                                            Case Study <ArrowRight className="h-3 w-3" />
                                        </div>
                                    </TiltCard>
                                </Link>
                            </FadeInStaggerItem>
                        );
                    })}

                </Stagger>

                {/* Mobile View All Link */}
                <div className="mt-12 md:hidden flex justify-center">
                    <Link
                        href="/case-studies"
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest border-b-2 border-black pb-1"
                    >
                        Explore all works <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
